import { DEFAULT_PROFILE_IMAGE_PATH } from 'common/ProviderPreview/constants'
import { getProviderProfileRoute } from 'dataSource/ProviderSearch/routes'
import Image from 'next/legacy/image'
import Link from 'next/link'
import { dateFormats, sessionStorageKeys } from 'constants/common'
import styles from './index.module.scss'

import _compact from 'lodash/compact'
import { useContext, useEffect, useMemo, useState } from 'react'
import _upperFirst from 'lodash/upperFirst'
import _isEmpty from 'lodash/isEmpty'
import { Button as CLButton } from 'component_library/1_Atoms'
import { Button } from 'react-bootstrap'
import { TimeZoneContext } from 'context/common/TimeZoneContext'
import useProviderListingDetails from 'hooks/provider/useProviderListingDetails-v3'
import _flatten from 'lodash/flatten'
import useAppSelector from 'hooks/useAppSelector'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import {
  getParsedQuery,
  getPracticeAddressFeatures,
  getStringifiedQuery,
} from 'utils/common'
import { useRouter } from 'next/router'
import useThriveAuth from 'hooks/useThriveAuth'
import credentials from 'common/ProviderCredentials/credentials'
import { Spinner } from 'react-bootstrap'
import { isNextOpeningSlotToday } from './utils'

type MobileListCardProviderSectionProps = {
  providerDetails: Provider
  areSlotsLoading: boolean
  generatedSlots: DateSlot
  nextAvailabilitySlot: ProviderSlot
  practice: Practice
  onNextOpeningClick: (
    selectedSlot: SelectedSlot,
    button_class: string,
    logNextOpeningRudderEvent: boolean
  ) => void
  showModal?: () => void
  renderCustomInsuranceSection?: () => JSX.Element
}

const MobileListCardProviderSection: React.FC<MobileListCardProviderSectionProps> = ({
  providerDetails,
  practice,
  areSlotsLoading,
  generatedSlots,
  nextAvailabilitySlot,
  onNextOpeningClick,
  showModal,
  renderCustomInsuranceSection,
}) => {
  const { globalGroupedFilters } = useAppSelector((state) => state.global)
  const { isSupportOrThirdPartyUser } = useThriveAuth()

  const router = useRouter()
  const { query } = router
  const [isAvailabilityInPerson, setIsAvailabilityInPerson] = useState<boolean>(
    false
  )
  const timeZoneInSession = sessionStorage.getItem(
    sessionStorageKeys.TIMEZONE_ABBR
  )
  const { firstName, lastName, pronouns, profileImagePath, title } =
    providerDetails ?? {}
  const {
    areaOfConcernValues,
    insuranceNamesValues,
  } = useProviderListingDetails(providerDetails, isSupportOrThirdPartyUser)
  const {
    city,
    state,
    streetAddress,
    aptOrSuite,
    zipcode,
  } = getPracticeAddressFeatures(practice)
  const availabilityTypeFilters =
    globalGroupedFilters[ProviderFilterTypes.AVAILABILITY_TYPE]

  const providerCredentials = credentials[title] || {}

  const nextAvailabilitySlotObj = useMemo(() => {
    const firstAvailableDateSlot = _flatten(Object.values(generatedSlots)).find(
      (date) => !!date
    )

    return firstAvailableDateSlot || nextAvailabilitySlot
  }, [generatedSlots])

  const { format, timeZoneObject } = useContext(TimeZoneContext)
  const providerProfileLink = getProviderProfileRoute(providerDetails)
  const formattedQuery = getParsedQuery(router)
  const queryObject = {
    ...(query?.insuranceId && { insuranceId: query.insuranceId }),
    ...(practice?.distanceInMiles && {
      distanceInMiles: practice.distanceInMiles.toFixed(2),
    }),
    ...(!_isEmpty(formattedQuery?.counselingType) && {
      counselingType: formattedQuery.counselingType,
    }),
    ...(!_isEmpty(formattedQuery?.availabilityType) && {
      availabilityType: formattedQuery.availabilityType[0],
    }),
    ...(timeZoneInSession && {
      tzAbbr: timeZoneInSession,
    }),
    ...(isSupportOrThirdPartyUser && {
      tzAbbr: timeZoneObject?.tzAbbr,
      tzCode: timeZoneObject?.tzCode,
    }),
  }
  const serializedQueryObject = getStringifiedQuery(queryObject)

  useEffect(() => {
    availabilityTypeFilters.map((avail) => {
      if (
        avail.id === queryObject?.availabilityType &&
        avail.value === 'In Person'
      ) {
        setIsAvailabilityInPerson(true)
      }
    })
  }, [queryObject])

  const capitalizedPronouns = pronouns?.map((pronoun) =>
    pronoun
      .split('/')
      .map((each) => each.charAt(0).toUpperCase() + each.slice(1))
      .join('/')
  )

  return (
    <div className={styles.ProviderCard}>
      <Link
        href={{
          pathname: providerProfileLink,
          query: serializedQueryObject,
        }}
        passHref
        legacyBehavior
      >
        <a href=" " className="text-decoration-none">
          <div className={styles.ProviderCard_container}>
            <div className={styles.ProviderCard_container_header}>
              <div
                className={styles.ProviderCard_container_header_providerImage}
              >
                <Image
                  unoptimized={!!profileImagePath}
                  src={profileImagePath || DEFAULT_PROFILE_IMAGE_PATH}
                  alt={`${firstName} ${lastName}'s profile image`}
                  layout="fill"
                  className="rounded-circle c-pointer"
                  objectFit="cover"
                  objectPosition="top"
                />
              </div>

              <div
                className={styles.ProviderCard_container_header_providerInfo}
              >
                <div
                  className={
                    styles.ProviderCard_container_header_providerInfo_container
                  }
                >
                  <h3>
                    <span
                      className={
                        styles.ProviderCard_container_header_providerInfo_container_name
                      }
                    >
                      {`${firstName} ${lastName}`}{' '}
                    </span>
                    {!!pronouns?.length && (
                      <span
                        className={
                          styles.ProviderCard_container_header_providerInfo_container_pronouns
                        }
                      >
                        {capitalizedPronouns}
                      </span>
                    )}
                  </h3>
                </div>

                <p className={styles.credentials}>
                  {providerCredentials.definition && (
                    <span>
                      {`${providerCredentials.definition}
                          ${
                            title !== providerCredentials.definition
                              ? ` (${title})`
                              : ''
                          }
                        `}
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className={styles.InfoContainer}>
              {renderCustomInsuranceSection
                ? renderCustomInsuranceSection()
                : insuranceNamesValues.length > 0 && (
                    <div className={styles.InfoContainer_insurance}>
                      <div className={styles.InfoContainer_insurance_icon}>
                        <img
                          src="/icons/shield-check.svg"
                          srcSet="/icons/shield-check1.webp"
                          alt="insurance"
                        />
                      </div>
                      <div className={styles.InfoContainer_insurance_list}>
                        {insuranceNamesValues.join(', ')}
                      </div>
                    </div>
                  )}
              {areaOfConcernValues.length > 0 && (
                <div className={styles.InfoContainer_speciality}>
                  <div className={styles.InfoContainer_speciality_icon}>
                    <img
                      src="/icons/armchair.svg"
                      srcSet="/icons/armchair.webp"
                      alt="speciality"
                    />
                  </div>
                  <div className={styles.InfoContainer_speciality_list}>
                    {areaOfConcernValues.join(', ')}
                  </div>
                </div>
              )}
              {isAvailabilityInPerson && !isSupportOrThirdPartyUser ? (
                <div className={styles.InfoContainer_availability}>
                  <div className={styles.InfoContainer_availability_icon}>
                    <img
                      src="/icons/map-pin.svg"
                      srcSet="/icons/map-pin.webp"
                      alt="address"
                    />
                  </div>
                  <div className={styles.InfoContainer_availability_list}>
                    {city}, {state} |{' '}
                    {`${practice.distanceInMiles.toFixed(2)}mi`}
                  </div>
                </div>
              ) : null}
              {isSupportOrThirdPartyUser && (
                <div className={styles.InfoContainer_availability}>
                  <div className={styles.InfoContainer_availability_icon}>
                    <img
                      src="/icons/map-pin.svg"
                      srcSet="/icons/map-pin.webp"
                      alt="address"
                    />
                  </div>

                  <div className={styles.InfoContainer_availability_list}>
                    <div>
                      {_compact([streetAddress, aptOrSuite]).join(', ')}
                    </div>
                    <div>
                      {city}, {state} {zipcode} |{' '}
                      {`${practice.distanceInMiles.toFixed(2)}mi`}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </a>
      </Link>
      <div className={styles.next_opening_container}>
        {/* always display the button when slots are loading to display loader inside the button
          if have nextAvailableDate then display the date inside the button else dont display the button */}
        {(nextAvailabilitySlotObj || areSlotsLoading) && (
          <CLButton
            theme="v2"
            variant="primary"
            full
            aria-label="Select next available opening slot"
            onClick={(event) =>
              onNextOpeningClick(
                {
                  slot: nextAvailabilitySlotObj,
                  provider: providerDetails,
                },
                (event.currentTarget as HTMLElement).className,
                true
              )
            }
            disabled={areSlotsLoading}
            className={styles.next_opening_btn}
          >
            {areSlotsLoading ? (
              <Spinner animation="border" />
            ) : isNextOpeningSlotToday(nextAvailabilitySlotObj) ? (
              `Next opening: Today, ${format(
                nextAvailabilitySlotObj?.start,
                ` ${dateFormats.TIME_FORMAT_LOWER}`
              ).replace(':00', '')}`
            ) : (
              `Next opening: ${format(
                nextAvailabilitySlotObj?.start,
                `${dateFormats.DAY_FORMAT} ${dateFormats.MONTH_DATE_SINGLEDIGIT_FORMAT}, ${dateFormats.TIME_FORMAT_LOWER}`
              ).replace(':00', '')}`
            )}
          </CLButton>
        )}
      </div>
      <Button
        variant="link"
        onClick={showModal}
        className={styles.view_all_availability_button}
      >
        View all availability
      </Button>
    </div>
  )
}

export default MobileListCardProviderSection
